import { EditOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, message, Row, Switch, Table, Tag } from "antd";
import { getColumnSearchProps } from "common/antd-utils";
import { PageHeader } from "components/pageHeader";
import {
  useFetchCommunicationResultItemsQuery,
  useUpdateCommunicationResultItemMutation,
} from "features/communicationResultItems/communicationResultItemsAPI";
import CommunicationResultItemModal from "features/communicationResultItems/components/communicationResultItemModal";
import { PERMISSIONS } from "features/permissions/constants";
import useAuthorizations from "features/permissions/hooks/useAuthorizations";
import { useRef, useState } from "react";
import styled from "styled-components";

const StyledHeader = styled(PageHeader)`
  margin-bottom: 0;
`;

const StyledRow = styled(Row)`
  margin-bottom: 8px;
`;

const RESULT_TYPE_LABELS = {
  call: "Call",
  text: "Text",
  email: "Email",
  voicemail: "Voicemail",
  mail: "Mail",
  communication: "Communication",
};

export default function CommunicationResultItemsTable({ resultType }) {
  const {
    data: {
      [PERMISSIONS.COMMUNICATION_RESULT_ITEM__CREATE]: isCreateAuthorized,
      [PERMISSIONS.COMMUNICATION_RESULT_ITEM__UPDATE]: isUpdateAuthorized,
    },
  } = useAuthorizations();

  const [isModalVisible, setModalVisible] = useState(false);
  const [activeRecord, setActiveRecord] = useState(null);

  const { data: resultItems = [], isLoading } = useFetchCommunicationResultItemsQuery({
    resultType,
  });

  const [updateCommunicationResultItem] = useUpdateCommunicationResultItemMutation();

  const onAddNew = () => {
    setActiveRecord(null);
    setModalVisible(true);
  };

  const onCloseModal = () => {
    setActiveRecord(null);
    setModalVisible(false);
  };

  const handleToggleEnabled = async (record) => {
    const result = await updateCommunicationResultItem({
      id: record.id,
      isEnabled: !record.isEnabled,
    });

    if ("data" in result) {
      message.success(
        `Communication result ${record.isEnabled ? "disabled" : "enabled"} successfully.`,
      );
    }
    if ("error" in result) {
      message.error(result?.error?.data?.error?.message);
    }
  };

  const [filteredInfo, setFilteredInfo] = useState({});
  const searchInput = useRef(null);

  const handleSearch = (selectedKeys, confirm) => {
    confirm();
  };

  const handleReset = (clearFilters) => {
    clearFilters();
  };

  const handleChange = (_, filters) => {
    setFilteredInfo(filters);
  };

  const columns = [
    {
      title: "Enabled",
      dataIndex: "isEnabled",
      key: "isEnabled",
      width: 90,
      render: (isEnabled, record) => (
        <Switch
          checked={isEnabled}
          onChange={() => handleToggleEnabled(record)}
          disabled={!isUpdateAuthorized}
        />
      ),
      sorter: (a, b) => b.isEnabled - a.isEnabled,
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: 300,
      render: (text) => text ?? "-",
      ...getColumnSearchProps("name", searchInput, handleSearch, handleReset),
      filteredValue: filteredInfo?.name ?? null,
      sorter: (a, b) => a.name.localeCompare(b.name),
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "Slug",
      dataIndex: "slug",
      key: "slug",
      width: 300,
      render: (text) => <code>{text}</code>,
      ...getColumnSearchProps("slug", searchInput, handleSearch, handleReset),
      filteredValue: filteredInfo?.slug ?? null,
      sorter: (a, b) => a.slug.localeCompare(b.slug),
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "Notes",
      dataIndex: "notes",
      key: "notes",
      width: 300,
      render: (text) => text ?? "-",
    },
    {
      title: "Type",
      dataIndex: "isSystem",
      key: "isSystem",
      width: 100,
      render: (isSystem) =>
        isSystem ? <Tag color="blue">System</Tag> : <Tag color="green">Custom</Tag>,
      sorter: (a, b) => b.isSystem - a.isSystem,
    },
    {
      title: "Actions",
      fixed: "right",
      key: "action",
      width: 80,
      align: "center",
      hidden: !isUpdateAuthorized,
      render: (_, record) =>
        record.isSystem ? null : (
          <EditOutlined
            onClick={() => {
              setActiveRecord(record);
              setModalVisible(true);
            }}
          />
        ),
    },
  ].filter((column) => !column.hidden);

  const typeLabel = RESULT_TYPE_LABELS[resultType] || resultType;

  return (
    <>
      <StyledHeader>{typeLabel} Results</StyledHeader>
      <StyledRow align="middle">
        <span>Manage {typeLabel.toLowerCase()} communication results.</span>
        {isCreateAuthorized && (
          <Button onClick={onAddNew} icon={<PlusOutlined />} type="link">
            Add Result
          </Button>
        )}
      </StyledRow>
      <Table
        loading={isLoading}
        bordered
        scroll={{ x: "max-content" }}
        columns={columns}
        dataSource={resultItems}
        onChange={handleChange}
        rowKey="id"
      />
      {isModalVisible && (
        <CommunicationResultItemModal
          title={activeRecord ? `Edit ${typeLabel} Result` : `Add ${typeLabel} Result`}
          onOk={onCloseModal}
          onCancel={onCloseModal}
          resultItem={activeRecord}
          resultType={resultType}
        />
      )}
    </>
  );
}
